import type { ContactPointLeaf } from "schema-dts";
import type { ChannelUnit, UnitPhone } from "../../s3";

const BRAZIL_COUNTRY_PREFIX = "55";
const TOLL_FREE_PREFIX = "0800";

/** Números nacionais têm no máximo 11 dígitos (DDD + celular). */
const MAX_NATIONAL_LENGTH = 11;

function onlyDigits(value: string): string {
  return value.replace(/\D/g, "");
}

/**
 * Mesma regra usada pelo `formatPhoneNumber` do WhatsappDropdown: só considera
 * o `55` como código de país quando sobra mais que um número nacional.
 */
function stripCountryCode(digits: string): string {
  return digits.startsWith(BRAZIL_COUNTRY_PREFIX) &&
    digits.length > MAX_NATIONAL_LENGTH
    ? digits.slice(BRAZIL_COUNTRY_PREFIX.length)
    : digits;
}

/** 0800 é discagem gratuita — vira `contactOption: "TollFree"` no schema.org. */
export function isTollFree(phoneNumber: string | null | undefined): boolean {
  if (!phoneNumber) return false;

  return stripCountryCode(onlyDigits(phoneNumber)).startsWith(TOLL_FREE_PREFIX);
}

/**
 * Telefone como os buscadores esperam.
 *
 * Números comuns saem em E.164 (`+5581999999999`) — são gravados como `55` +
 * dígitos, faltava só o `+`. Já um 0800 sai **sem** código de país: é discagem
 * nacional, não é alcançável de fora do Brasil, e prefixar `+55` produziria um
 * E.164 que não existe.
 */
export function toTelephone(
  phoneNumber: string | null | undefined,
): string | undefined {
  if (!phoneNumber) return undefined;

  const trimmed = phoneNumber.trim();
  if (trimmed.startsWith("+")) return trimmed;

  const digits = onlyDigits(trimmed);
  if (!digits) return undefined;

  const national = stripCountryCode(digits);

  return national.startsWith(TOLL_FREE_PREFIX) ? national : `+${digits}`;
}

/**
 * Percorre a árvore de telefones em ordem, pai antes dos filhos. Cada nó carrega
 * o próprio `name` como rótulo do número (é o que a tela pede ao cadastrar:
 * "o nome que identificará o número"), então filhos não herdam o rótulo do pai.
 */
function flattenPhones(phones: UnitPhone[] | null | undefined): UnitPhone[] {
  if (!Array.isArray(phones)) return [];

  return [...phones]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .flatMap((phone) => [phone, ...flattenPhones(phone.children)]);
}

/**
 * Um `ContactPoint` por número cadastrado na unidade. É o que expõe os telefones
 * secundários — o `telephone` plano do nó carrega apenas o principal.
 *
 * `contactType` recebe o rótulo como foi digitado: `contactType` é texto livre no
 * schema.org, sem vocabulário normativo, então traduzir seria convenção nossa.
 */
export function buildContactPoints(unit: ChannelUnit): ContactPointLeaf[] {
  return flattenPhones(unit.phones)
    .map((phone) => ({
      telephone: toTelephone(phone.phoneNumber),
      name: phone.name,
      tollFree: isTollFree(phone.phoneNumber),
    }))
    .filter((phone) => Boolean(phone.telephone))
    .map(({ telephone, name, tollFree }) => ({
      "@type": "ContactPoint" as const,
      telephone,
      contactType: name?.trim() || undefined,
      contactOption: tollFree ? ("TollFree" as const) : undefined,
    }));
}
